import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  CurrencyDollar, Buildings, Users, ChartLineUp, ArrowUp, ArrowDown,
  DownloadSimple, CalendarBlank, TrendUp
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { toast } from 'sonner';

const formatCLP = (amount) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency', currency: 'CLP',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount);

const MONTHS = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];

const LaborCostPage = () => {
  const { axiosInstance, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState('');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [activeTab, setActiveTab] = useState('resumen');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { year, month };
      if (selectedBranch) params.branch_id = selectedBranch;

      const [costRes, branchesRes] = await Promise.all([
        axiosInstance.get('/api/labor-cost/summary', { params }),
        isAdmin ? axiosInstance.get('/api/branches') : Promise.resolve({ data: [] }),
      ]);
      setData(costRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      // error handled silently
      toast.error('Error al cargar datos de costo laboral');
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, isAdmin, year, month, selectedBranch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (format = 'xlsx') => {
    try {
      const params = { year, month, format };
      if (selectedBranch) params.branch_id = selectedBranch;
      const response = await axiosInstance.get('/api/export/labor-cost', {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `costo_laboral_${year}_${String(month).padStart(2,'0')}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exportación descargada');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const tabs = [
    { key: 'resumen', label: 'Resumen' },
    { key: 'sucursales', label: 'Por Sucursal' },
    { key: 'trabajadores', label: 'Por Trabajador' },
  ];

  return (
    <div className="space-y-6" data-testid="labor-cost-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="labor-cost-title">
            Costo Laboral
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Control de costo laboral diario, semanal y mensual
          </p>
        </div>
        <button
          onClick={() => handleExport('xlsx')}
          className="btn-primary flex items-center gap-2"
          data-testid="export-labor-cost-btn"
        >
          <DownloadSimple size={18} />
          Exportar Excel
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <CalendarBlank size={18} className="text-zinc-400" />
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            className="form-select w-36"
            data-testid="month-filter"
          >
            {MONTHS.map((m, idx) => (
              <option key={m} value={idx + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="form-select w-24"
            data-testid="year-filter"
          >
            {[2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        {isAdmin && (
          <select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            className="form-select w-48"
            data-testid="branch-filter"
          >
            <option value="">Todas las sucursales</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}
        {data?.vacations_count_as_cost && (
          <span className="text-xs px-2 py-1 bg-amber-50 border border-amber-200 text-amber-700" data-testid="vacations-cost-badge">
            Vacaciones incluidas en costo
          </span>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border border-zinc-200 bg-white p-5" data-testid="kpi-today">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Hoy</span>
            <CurrencyDollar size={20} className="text-zinc-400" />
          </div>
          <p className="text-2xl font-bold font-mono text-zinc-900">{formatCLP(kpis.today || 0)}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-5" data-testid="kpi-week">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Semana</span>
            <TrendUp size={20} className="text-zinc-400" />
          </div>
          <p className="text-2xl font-bold font-mono text-zinc-900">{formatCLP(kpis.week || 0)}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-5" data-testid="kpi-month">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Mes</span>
            <ChartLineUp size={20} className="text-zinc-400" />
          </div>
          <p className="text-2xl font-bold font-mono text-zinc-900">{formatCLP(kpis.month || 0)}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-5" data-testid="kpi-avg-daily">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Promedio Diario</span>
            <CalendarBlank size={20} className="text-zinc-400" />
          </div>
          <p className="text-2xl font-bold font-mono text-zinc-900">{formatCLP(kpis.avg_daily || 0)}</p>
          <p className="text-xs text-zinc-400 mt-1">{kpis.workers_with_cost || 0} de {kpis.total_workers || 0} con costo</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'border-b-2 border-zinc-900 text-zinc-900'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'resumen' && (
        <div className="space-y-6">
          {/* Daily Trend Chart */}
          <div className="border border-zinc-200 bg-white p-6">
            <h2 className="font-heading font-semibold text-zinc-900 mb-4">Tendencia Diaria del Mes</h2>
            <div style={{ width: '100%', height: 300 }} data-testid="daily-trend-chart">
              <ResponsiveContainer>
                <AreaChart data={data?.daily_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#a1a1aa" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#a1a1aa" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value) => [formatCLP(value), 'Costo']}
                    labelFormatter={(label) => `Día ${label}`}
                    contentStyle={{ border: '1px solid #e4e4e7', borderRadius: 0, fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="cost" stroke="#18181b" fill="#18181b" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Branch summary cards */}
          <div className="border border-zinc-200 bg-white p-6">
            <h2 className="font-heading font-semibold text-zinc-900 mb-4">Costo por Sucursal (Hoy)</h2>
            {(data?.branch_today || []).length === 0 ? (
              <p className="text-sm text-zinc-400 text-center py-4">Sin datos para hoy</p>
            ) : (
              <div className="space-y-2">
                {data.branch_today.map((b) => {
                  const maxCost = Math.max(...data.branch_today.map(x => x.cost), 1);
                  const pct = (b.cost / maxCost) * 100;
                  return (
                    <div key={b.branch} className="flex items-center gap-3" data-testid={`branch-today-${b.branch}`}>
                      <span className="text-sm text-zinc-600 w-40 truncate">{b.branch}</span>
                      <div className="flex-1 bg-zinc-100 h-6 relative">
                        <div
                          className="bg-zinc-800 h-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-sm font-mono font-medium text-zinc-900 w-28 text-right">
                        {formatCLP(b.cost)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'sucursales' && (
        <div className="space-y-6">
          {/* Branch Monthly Ranking */}
          <div className="border border-zinc-200 bg-white p-6">
            <h2 className="font-heading font-semibold text-zinc-900 mb-4">Ranking Mensual por Sucursal</h2>
            <div style={{ width: '100%', height: Math.max(200, (data?.branch_monthly?.length || 1) * 60) }} data-testid="branch-monthly-chart">
              <ResponsiveContainer>
                <BarChart data={data?.branch_monthly || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#a1a1aa" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="branch" width={140} tick={{ fontSize: 12 }} stroke="#a1a1aa" />
                  <Tooltip
                    formatter={(value) => [formatCLP(value), 'Costo Mensual']}
                    contentStyle={{ border: '1px solid #e4e4e7', borderRadius: 0, fontSize: 12 }}
                  />
                  <Bar dataKey="cost" fill="#18181b" barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Branch table */}
          <div className="border border-zinc-200 bg-white">
            <table className="w-full" data-testid="branch-cost-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">Sucursal</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">Costo Mensual</th>
                </tr>
              </thead>
              <tbody>
                {(data?.branch_monthly || []).map((b) => (
                  <tr key={b.branch} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-zinc-900">{b.branch}</td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-zinc-900 font-semibold">
                      {formatCLP(b.cost)}
                    </td>
                  </tr>
                ))}
                {(data?.branch_monthly || []).length > 0 && (
                  <tr className="bg-zinc-50 font-bold">
                    <td className="px-4 py-3 text-sm text-zinc-900">Total</td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-zinc-900">
                      {formatCLP(kpis.month || 0)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'trabajadores' && (
        <div className="space-y-6">
          {/* Top Workers */}
          <div className="border border-zinc-200 bg-white">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
              <ArrowUp size={16} className="text-emerald-600" />
              <h2 className="font-heading font-semibold text-zinc-900 text-sm">Mayor Costo Laboral</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="top-workers-table">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">#</th>
                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Trabajador</th>
                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Sucursal</th>
                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Cargo</th>
                    <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Valor Día</th>
                    <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Costo Mensual</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.worker_ranking || []).map((w, idx) => (
                    <tr key={w.id} className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${w.status === 'desvinculado' ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-2.5 text-sm text-zinc-400">{idx + 1}</td>
                      <td className="px-4 py-2.5 text-sm font-medium text-zinc-900">{w.name}</td>
                      <td className="px-4 py-2.5 text-sm text-zinc-600">{w.branch}</td>
                      <td className="px-4 py-2.5 text-sm text-zinc-600">{w.cargo}</td>
                      <td className="px-4 py-2.5 text-sm font-mono text-right text-zinc-600">{formatCLP(w.valor_dia)}</td>
                      <td className="px-4 py-2.5 text-sm font-mono text-right font-semibold text-zinc-900">{formatCLP(w.costo_mensual)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom Workers */}
          {(data?.worker_bottom || []).length > 0 && (
            <div className="border border-zinc-200 bg-white">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
                <ArrowDown size={16} className="text-amber-600" />
                <h2 className="font-heading font-semibold text-zinc-900 text-sm">Menor Costo Laboral (con valor día asignado)</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="bottom-workers-table">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">#</th>
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Trabajador</th>
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Sucursal</th>
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Cargo</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Valor Día</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Costo Mensual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.worker_bottom.map((w, idx) => (
                      <tr key={w.id} className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${w.status === 'desvinculado' ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-2.5 text-sm text-zinc-400">{idx + 1}</td>
                        <td className="px-4 py-2.5 text-sm font-medium text-zinc-900">{w.name}</td>
                        <td className="px-4 py-2.5 text-sm text-zinc-600">{w.branch}</td>
                        <td className="px-4 py-2.5 text-sm text-zinc-600">{w.cargo}</td>
                        <td className="px-4 py-2.5 text-sm font-mono text-right text-zinc-600">{formatCLP(w.valor_dia)}</td>
                        <td className="px-4 py-2.5 text-sm font-mono text-right font-semibold text-zinc-900">{formatCLP(w.costo_mensual)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LaborCostPage;
